import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  E_COM_SIGNUP,
  ACCOUNT_ID,
  ACH_ACCOUNT,
  ACH_ACCOUNT_NAME,
  ACH_BANK_NAME,
  ACH_ROUTING,
  AUTO_BILLING_ENROLL,
  AUTO_BILLING_LA,
  AUTO_BILLING_LA_END_DATE,
  AUTO_BILLING_LA_START_DATE,
  AUTO_BILLING_START_DATE,
  E_ACCOUNT_EMAIL,
  E_ACCOUNT_NAME,
  E_ACCOUNT_NO,
  E_ACCOUNT_PHONE,
  E_COM_START_DATE,
  PAYMENT_TYPE,
  ZELLE_URL,
} from "./consts";
import {
  useAddOwnerAccountDtlMutation,
  useGetOwnerAccountDtlByOwnershipId,
  useUpdateOwnerAccountDtlMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useEffect, useState } from "react";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { schema } from "./validations";

const useStep4 = () => {
  const [key, setKey] = useState<number>(Math.random() * 3);
  const form = useForm<any>({
    defaultValues: {
      [ACCOUNT_ID]: "",
      [PAYMENT_TYPE]: "",
      [ZELLE_URL]: undefined,
      [ACH_ROUTING]: undefined,
      [ACH_ACCOUNT]: undefined,
      [ACH_ACCOUNT_NAME]: undefined,
      [ACH_BANK_NAME]: undefined,
      [E_ACCOUNT_NO]: undefined,
      [E_ACCOUNT_NAME]: undefined,
      [E_ACCOUNT_EMAIL]: undefined,
      [E_ACCOUNT_PHONE]: undefined,
      [E_COM_SIGNUP]: false,
      [E_COM_START_DATE]: undefined,
      [AUTO_BILLING_ENROLL]: false,
      [AUTO_BILLING_START_DATE]: undefined,
      [AUTO_BILLING_LA]: false,
      [AUTO_BILLING_LA_START_DATE]: undefined,
      [AUTO_BILLING_LA_END_DATE]: undefined,
    },
    resolver: zodResolver(schema),
  });

  const {loggedInUserData, communityData} = useSelector(
    (state: RootState) => state.currentData
  );

  const userId = loggedInUserData?.userId;
  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  // get owner account details
  const {
    data: ownerAccountDtlDetails,
    isLoading: isOwnerAccountDtlDetailsLoading,
    isRefetching: isOwnerAccountDtlDetailsRefetching,
  } = useGetOwnerAccountDtlByOwnershipId({
    id: userId as string,
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!(userId && communityData?.communityId),
    },
  });

  useEffect(() => {
    if (ownerAccountDtlDetails) {
      form.reset({
        [ACCOUNT_ID]:
          ownerAccountDtlDetails?.data?.[ACCOUNT_ID]?.toString() || "",
        [PAYMENT_TYPE]:
          ownerAccountDtlDetails?.data?.[PAYMENT_TYPE]?.toString() || undefined,
        [ZELLE_URL]: ownerAccountDtlDetails?.data?.[ZELLE_URL] || undefined,
        [ACH_ROUTING]: ownerAccountDtlDetails?.data?.[ACH_ROUTING] || undefined,
        [ACH_ACCOUNT]: ownerAccountDtlDetails?.data?.[ACH_ACCOUNT] || undefined,
        [ACH_ACCOUNT_NAME]:
          ownerAccountDtlDetails?.data?.[ACH_ACCOUNT_NAME] || undefined,
        [ACH_BANK_NAME]:
          ownerAccountDtlDetails?.data?.[ACH_BANK_NAME] || undefined,
        [E_ACCOUNT_NO]:
          ownerAccountDtlDetails?.data?.[E_ACCOUNT_NO] || undefined,
        [E_ACCOUNT_NAME]:
          ownerAccountDtlDetails?.data?.[E_ACCOUNT_NAME] || undefined,
        [E_ACCOUNT_EMAIL]:
          ownerAccountDtlDetails?.data?.[E_ACCOUNT_EMAIL] || undefined,
        [E_ACCOUNT_PHONE]:
          ownerAccountDtlDetails?.data?.[E_ACCOUNT_PHONE] || undefined,
        [E_COM_SIGNUP]:
          ownerAccountDtlDetails?.data?.[E_COM_SIGNUP] || false,
        [E_COM_START_DATE]:
          ownerAccountDtlDetails?.data?.[E_COM_START_DATE] || undefined,
        [AUTO_BILLING_ENROLL]:
          ownerAccountDtlDetails?.data?.[AUTO_BILLING_ENROLL] || false,
        [AUTO_BILLING_START_DATE]:
          ownerAccountDtlDetails?.data?.[AUTO_BILLING_START_DATE] || undefined,
        [AUTO_BILLING_LA]:
          ownerAccountDtlDetails?.data?.[AUTO_BILLING_LA] || false,
        [AUTO_BILLING_LA_START_DATE]:
          ownerAccountDtlDetails?.data?.[AUTO_BILLING_LA_START_DATE] ||
          undefined,
        [AUTO_BILLING_LA_END_DATE]:
          ownerAccountDtlDetails?.data?.[AUTO_BILLING_LA_END_DATE] || undefined,
      });
      setKey(Math.random() * 3);
    }
  }, [ownerAccountDtlDetails]);

  // Add Owner to Property
  const { mutate: addOwnerAccountDtl, isPending: isAddOwnerAccountDtlPending } =
    useAddOwnerAccountDtlMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Accounting Information Added Successfully");
      },
    });

  // Add User Info
  const { mutate: updatePreference, isPending: isUpdatePreferencePending } =
    useUpdateOwnerAccountDtlMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Accounting Information Updated Successfully");
      },
    });

    const validateFields = (_data: any) => {
      if (_data[AUTO_BILLING_ENROLL]) {
        if (!_data[AUTO_BILLING_START_DATE]) {
          form.setError(AUTO_BILLING_START_DATE, {
            message: "This field is required.",
          });
          return false;
        }
      } else {
        _data[AUTO_BILLING_START_DATE] = null;
      }
  
      if (_data[AUTO_BILLING_LA]) {
        if (!_data[AUTO_BILLING_LA_START_DATE]) {
          form.setError(AUTO_BILLING_LA_START_DATE, {
            message: "This field is required.",
          });
          return false;
        }
      } else {
        _data[AUTO_BILLING_LA_START_DATE] = null;
        _data[AUTO_BILLING_LA_END_DATE] = null;
      }
  
      if (_data[E_COM_SIGNUP]) {
        if (!_data[E_COM_START_DATE]) {
          form.setError(E_COM_START_DATE, {
            message: "This field is required.",
          });
          return false;
        }
      } else {
        _data[E_COM_START_DATE] = null;
      }

    // if (_data[PAYMENT_TYPE] === "ACH") {
    //   if (!_data[ACH_ROUTING]) {
    //     form.setError(ACH_ROUTING, {
    //       message: "This field is required.",
    //     });
    //     return false;
    //   }
    //   if (!_data[ACH_ACCOUNT]) {
    //     form.setError(ACH_ACCOUNT, {
    //       message: "This field is required.",
    //     });
    //     return false;
    //   }
    //   if (!_data[ACH_ACCOUNT_NAME]) {
    //     form.setError(ACH_ACCOUNT_NAME, {
    //       message: "This field is required.",
    //     });
    //     return false;
    //   }
    //   if (!_data[ACH_BANK_NAME]) {
    //     form.setError(ACH_BANK_NAME, {
    //       message: "This field is required.",
    //     });
    //     return false;
    //   }
    // }

    // if (_data[PAYMENT_TYPE] === "ZELLE") {
    //   if (!_data[ZELLE_URL]) {
    //     form.setError(ZELLE_URL, {
    //       message: "This field is required.",
    //     });
    //     return false;
    //   }

    //   if (!_data[ZELLE_URL]?.toLowerCase()?.includes("zelle")) {
    //     form.setError(ZELLE_URL, {
    //       message: "Invalid zelle url.",
    //     });
    //     return false;
    //   }
    // }
    return true;
  };

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    const isValid = validateFields(_data);

    delete _data[ACCOUNT_ID];
    if (isValid) {
      if (ownerAccountDtlDetails?.data) {
        updatePreference({
          id: ownerAccountDtlDetails?.data?.accountDetailId,
          data: {
            ..._data,
            communityId: communityData?.communityId,
            userId: loggedInUserData?.userId,
          },
        });
      } else {
        addOwnerAccountDtl({
          data: {
            ..._data,
            communityId: communityData?.communityId,
            userId: loggedInUserData?.userId,
          },
        });
      }
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isOwnerAccountDtlDetailsLoading,
    isAddOwnerAccountDtlPending,
    isUpdatePreferencePending,
    userId,
    ownerAccountDtlDetails,
    key,
    isOwnerAccountDtlDetailsRefetching,
  };
};

export default useStep4;
